import React, { useState } from 'react';
import {
    Box, Container, Stepper, Step, StepLabel, Button, Typography, TextField, FormControl, RadioGroup, FormControlLabel, Radio, FormLabel, Card,
    CardContent, GridLegacy as Grid, Chip, LinearProgress, Checkbox, styled, alpha, useTheme, useMediaQuery
} from '@mui/material';
import { Star, AutoAwesome, Psychology, TrendingUp, ChevronLeft, ChevronRight, Check, Celebration } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';

// Types
interface OnboardingData {
    // Personal Info
    firstName: string;
    lastName: string;
    email: string;

    // Birth Details
    birthDate: Date | null;
    birthTime: string;
    birthLocation: string;
    timeZone: string;
    isExactTime: boolean;

    // Interests
    primaryInterests: string[];
    lifeAreas: string[];
    experience: string;

    // Goals
    goals: string[];
    readingFrequency: string;

    // Plan Selection
    selectedPlan: 'free' | 'pro' | 'elite' | '';
}

interface StepConfig {
    title: string;
    subtitle: string;
    icon: React.ReactNode;
    completed: boolean;
}

// Custom theme colors (using your brand palette)
const brandColors = {
    primary: {
        main: '#3B82F6',
        light: '#60A5FA',
        dark: '#1E40AF',
    },
    secondary: {
        main: '#8B5CF6',
        light: '#A78BFA',
        dark: '#6D28D9',
    },
    accent: {
        main: '#F97316',
        light: '#FB923C',
        dark: '#EA580C',
    }
};

// Styled Components
const OnboardingContainer = styled(Box)(({ theme }) => ({
    minHeight: '100vh',
    background: `linear-gradient(135deg, 
    #030712 0%, 
    ${alpha(brandColors.primary.dark, 0.1)} 25%,
    ${alpha(brandColors.secondary.dark, 0.05)} 75%,
    #030712 100%)`,
    position: 'relative',
    overflow: 'hidden',
}));

const StepCard = styled(Card)(({ theme }) => ({
    background: `linear-gradient(135deg, 
    ${alpha('#1E293B', 0.8)} 0%, 
    ${alpha(brandColors.primary.main, 0.05)} 100%)`,
    backdropFilter: 'blur(20px)',
    border: `1px solid ${alpha(brandColors.primary.main, 0.2)}`,
    borderRadius: '24px',
    boxShadow: `0 20px 40px ${alpha(brandColors.primary.main, 0.1)}`,
}));

const PlanCard = styled(Card)<{ selected?: boolean; featured?: boolean }>(({ theme, selected, featured }) => ({
    background: selected
        ? `linear-gradient(135deg, ${alpha(brandColors.primary.main, 0.2)}, ${alpha(brandColors.secondary.main, 0.1)})`
        : `linear-gradient(135deg, ${alpha('#1E293B', 0.6)}, ${alpha('#1E293B', 0.4)})`,
    backdropFilter: 'blur(20px)',
    border: selected
        ? `2px solid ${brandColors.primary.main}`
        : `1px solid ${alpha(brandColors.primary.main, 0.2)}`,
    borderRadius: '20px',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    transform: featured ? 'scale(1.05)' : 'none',
    position: 'relative',
    '&:hover': {
        transform: featured ? 'scale(1.08)' : 'scale(1.02)',
        borderColor: brandColors.primary.light,
    },
    ...(featured && {
        '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: `linear-gradient(90deg, ${brandColors.primary.main}, ${brandColors.secondary.main})`,
            borderRadius: '20px 20px 0 0',
        }
    })
}));

const InterestChip = styled(Chip)<{ selected?: boolean }>(({ theme, selected }) => ({
    background: selected
        ? `linear-gradient(135deg, ${brandColors.primary.main}, ${brandColors.secondary.main})`
        : alpha('#1E293B', 0.6),
    color: selected ? 'white' : '#CBD5E1',
    border: `1px solid ${selected ? 'transparent' : alpha(brandColors.primary.main, 0.3)}`,
    '&:hover': {
        background: selected
            ? `linear-gradient(135deg, ${brandColors.primary.light}, ${brandColors.secondary.light})`
            : alpha(brandColors.primary.main, 0.1),
    },
    transition: 'all 0.3s ease',
}));

const BrandButton = styled(Button)(({ theme }) => ({
    background: `linear-gradient(135deg, ${brandColors.primary.main}, ${brandColors.secondary.main})`,
    color: 'white',
    fontWeight: 600,
    padding: '12px 32px',
    borderRadius: '50px',
    textTransform: 'none',
    fontSize: '1.1rem',
    gap: '0 16px',
    '&:hover': {
        background: `linear-gradient(135deg, ${brandColors.primary.light}, ${brandColors.secondary.light})`,
        transform: 'translateY(-2px)',
        boxShadow: `0 10px 30px ${alpha(brandColors.primary.main, 0.4)}`,
    },
    '&:disabled': {
        background: alpha('#1E293B', 0.6),
        color: '#64748B',
    },
    transition: 'all 0.3s ease',
    '& .MuiButton-endIcon, & .MuiButton-startIcon': {
        marginInlineStart: '12px',
        marginInlineEnd: '0px',
    },
    '[dir="rtl"] & .MuiButton-endIcon, [dir="rtl"] & .MuiButton-startIcon': {
        marginInlineStart: '0px',
        marginInlineEnd: '12px',
    },
}));

const OnBoarding: React.FC = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { t } = useTranslation();
    const { isRTL } = useLanguage();

    const [activeStep, setActiveStep] = useState(0);
    const [onboardingData, setOnboardingData] = useState<OnboardingData>({
        firstName: '',
        lastName: '',
        email: '',
        birthDate: null,
        birthTime: '',
        birthLocation: '',
        timeZone: '',
        isExactTime: true,
        primaryInterests: [],
        lifeAreas: [],
        experience: '',
        goals: [],
        readingFrequency: '',
        selectedPlan: 'pro'
    });
    const [emailError, setEmailError] = useState<string>('');

    const validateEmail = (email: string) => {
        // Simple email regex
        return /^\S+@\S+\.\S+$/.test(email);
    };

    const updateData = (field: keyof OnboardingData, value: any) => {
        setOnboardingData(prev => ({ ...prev, [field]: value }));
        if (field === 'email') {
            if (!validateEmail(value)) {
                setEmailError(t('onboarding.form.emailInvalid') || 'Invalid email address');
            } else {
                setEmailError('');
            }
        }
    };

    const toggleArrayItem = (field: keyof OnboardingData, item: string) => {
        const currentArray = onboardingData[field] as string[];
        const newArray = currentArray.includes(item)
            ? currentArray.filter(i => i !== item)
            : [...currentArray, item];
        updateData(field, newArray);
    };

    // Step configuration
    const steps: StepConfig[] = [
        {
            title: t('onboarding.steps.personalInfo.title'),
            subtitle: t('onboarding.steps.personalInfo.subtitle'),
            icon: <Psychology />,
            completed: Boolean(onboardingData.firstName && onboardingData.lastName && onboardingData.email)
        },
        {
            title: t('onboarding.steps.birthDetails.title'),
            subtitle: t('onboarding.steps.birthDetails.subtitle'),
            icon: <Star />,
            completed: Boolean(onboardingData.birthDate && onboardingData.birthTime && onboardingData.birthLocation)
        },
        {
            title: t('onboarding.steps.interests.title'),
            subtitle: t('onboarding.steps.interests.subtitle'),
            icon: <AutoAwesome />,
            completed: Boolean(onboardingData.primaryInterests.length > 0 && onboardingData.experience)
        },
        {
            title: t('onboarding.steps.goals.title'),
            subtitle: t('onboarding.steps.goals.subtitle'),
            icon: <TrendingUp />,
            completed: Boolean(onboardingData.goals.length > 0 && onboardingData.readingFrequency)
        },
        {
            title: t('onboarding.steps.plan.title'),
            subtitle: t('onboarding.steps.plan.subtitle'),
            icon: <Celebration />,
            completed: onboardingData.selectedPlan !== ''
        }
    ];

    // Data for form options
    const interests = [
        t('onboarding.interests.dailyHoroscopes'),
        t('onboarding.interests.loveRelationships'),
        t('onboarding.interests.careerGuidance'),
        t('onboarding.interests.personalGrowth'),
        t('onboarding.interests.spiritualJourney'),
        t('onboarding.interests.healthWellness'),
        t('onboarding.interests.financialInsights'),
        t('onboarding.interests.familyDynamics'),
        t('onboarding.interests.creativeExpression'),
        t('onboarding.interests.lifeTransitions'),
        t('onboarding.interests.dreamInterpretation'),
        t('onboarding.interests.meditation')
    ];

    const lifeAreas = [
        t('onboarding.lifeAreas.careerWork'),
        t('onboarding.lifeAreas.loveRomance'),
        t('onboarding.lifeAreas.familyHome'),
        t('onboarding.lifeAreas.healthWellness'),
        t('onboarding.lifeAreas.moneyFinance'),
        t('onboarding.lifeAreas.spirituality'),
        t('onboarding.lifeAreas.creativity'),
        t('onboarding.lifeAreas.travelAdventure'),
        t('onboarding.lifeAreas.education'),
        t('onboarding.lifeAreas.friendships'),
        t('onboarding.lifeAreas.personalGrowth'),
        t('onboarding.lifeAreas.lifePurpose')
    ];

    const goals = [
        t('onboarding.goals.understandSelf'),
        t('onboarding.goals.improveRelationships'),
        t('onboarding.goals.betterDecisions'),
        t('onboarding.goals.findPurpose'),
        t('onboarding.goals.enhanceIntuition'),
        t('onboarding.goals.navigateChallenges'),
        t('onboarding.goals.planEvents'),
        t('onboarding.goals.spiritualGrowth'),
        t('onboarding.goals.careerGuidance'),
        t('onboarding.goals.healthInsights'),
        t('onboarding.goals.timingDecisions')
    ];

    // const zodiacSigns = [
    //     'Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo',
    //     'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'
    // ];

    const isStepValid = (step: number): boolean => {
        switch (step) {
            case 0:
                return !!(onboardingData.firstName && onboardingData.lastName && onboardingData.email && !emailError && validateEmail(onboardingData.email));
            case 1:
                return !!(onboardingData.birthDate && onboardingData.birthTime && onboardingData.birthLocation);
            case 2:
                return Boolean(onboardingData.primaryInterests.length > 0 && onboardingData.experience);
            case 3:
                return Boolean(onboardingData.goals.length > 0 && onboardingData.readingFrequency);
            case 4:
                return !!onboardingData.selectedPlan;
            default:
                return false;
        }
    };

    const handleNext = () => {
        if (activeStep < steps.length - 1) {
            setActiveStep(prev => prev + 1);
        }
    };

    const handleBack = () => {
        if (activeStep > 0) {
            setActiveStep(prev => prev - 1);
        }
    };

    const handleComplete = () => {
        // Handle onboarding completion
        console.log('Onboarding completed:', onboardingData);
        // In real app: send data to API, redirect to dashboard, etc.
    };

    const renderStepContent = (step: number) => {
        switch (step) {
            case 0:
                return (
                    <Box sx={{ space: 3 }}>
                        <Typography variant="h4" sx={{ fontWeight: 700, mb: 2, color: 'white' }}>
                            {t('onboarding.welcome.title')}
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 4, color: '#CBD5E1' }}>
                            {t('onboarding.welcome.description')}
                        </Typography>

                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label={t('onboarding.form.firstName')}
                                    value={onboardingData.firstName}
                                    onChange={(e) => updateData('firstName', e.target.value)}
                                    inputProps={{
                                        style: {
                                            textAlign: isRTL ? 'right' : 'left'
                                        }
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            backgroundColor: alpha('#1E293B', 0.6),
                                            '& fieldset': { borderColor: alpha(brandColors.primary.main, 0.3) },
                                            '&:hover fieldset': { borderColor: brandColors.primary.main },
                                            '&.Mui-focused fieldset': { borderColor: brandColors.primary.light },
                                        },
                                        '& .MuiInputLabel-root': { color: '#CBD5E1' },
                                        '& .MuiInputBase-input': { color: 'white' },
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label={t('onboarding.form.lastName')}
                                    value={onboardingData.lastName}
                                    onChange={(e) => updateData('lastName', e.target.value)}
                                    inputProps={{
                                        style: {
                                            textAlign: isRTL ? 'right' : 'left'
                                        }
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            backgroundColor: alpha('#1E293B', 0.6),
                                            '& fieldset': { borderColor: alpha(brandColors.primary.main, 0.3) },
                                            '&:hover fieldset': { borderColor: brandColors.primary.main },
                                            '&.Mui-focused fieldset': { borderColor: brandColors.primary.light },
                                        },
                                        '& .MuiInputLabel-root': { color: '#CBD5E1' },
                                        '& .MuiInputBase-input': { color: 'white' },
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label={t('onboarding.form.email')}
                                    type="email"
                                    value={onboardingData.email}
                                    onChange={(e) => updateData('email', e.target.value)}
                                    error={!!emailError}
                                    helperText={emailError}
                                    inputProps={{
                                        style: {
                                            textAlign: isRTL ? 'right' : 'left'
                                        }
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            backgroundColor: alpha('#1E293B', 0.6),
                                            '& fieldset': { borderColor: alpha(brandColors.primary.main, 0.3) },
                                            '&:hover fieldset': { borderColor: brandColors.primary.main },
                                            '&.Mui-focused fieldset': { borderColor: brandColors.primary.light },
                                        },
                                        '& .MuiInputLabel-root': { color: '#CBD5E1' },
                                        '& .MuiInputBase-input': { color: 'white' },
                                    }}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                );

            case 1:
                return (
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 700, mb: 2, color: 'white' }}>
                            {t('onboarding.birthChart.title')}
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 4, color: '#CBD5E1' }}>
                            {t('onboarding.birthChart.description')}
                        </Typography>

                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label={t('onboarding.form.birthDate')}
                                    type="date"
                                    value={onboardingData.birthDate ? onboardingData.birthDate.toISOString().split('T')[0] : ''}
                                    onChange={(e) => updateData('birthDate', e.target.value ? new Date(e.target.value) : null)}
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{
                                        style: {
                                            textAlign: isRTL ? 'right' : 'left'
                                        }
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            backgroundColor: alpha('#1E293B', 0.6),
                                            '& fieldset': { borderColor: alpha(brandColors.primary.main, 0.3) },
                                            '&:hover fieldset': { borderColor: brandColors.primary.main },
                                            '&.Mui-focused fieldset': { borderColor: brandColors.primary.light },
                                        },
                                        '& .MuiInputLabel-root': { color: '#CBD5E1' },
                                        '& .MuiInputBase-input': { color: 'white' },
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label={t('onboarding.form.birthTime')}
                                    type="time"
                                    value={onboardingData.birthTime}
                                    onChange={(e) => updateData('birthTime', e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{
                                        style: {
                                            textAlign: isRTL ? 'right' : 'left'
                                        }
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            backgroundColor: alpha('#1E293B', 0.6),
                                            '& fieldset': { borderColor: alpha(brandColors.primary.main, 0.3) },
                                            '&:hover fieldset': { borderColor: brandColors.primary.main },
                                            '&.Mui-focused fieldset': { borderColor: brandColors.primary.light },
                                        },
                                        '& .MuiInputLabel-root': { color: '#CBD5E1' },
                                        '& .MuiInputBase-input': { color: 'white' },
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label={t('onboarding.form.birthLocation')}
                                    value={onboardingData.birthLocation}
                                    onChange={(e) => updateData('birthLocation', e.target.value)}
                                    placeholder={t('onboarding.form.birthLocationPlaceholder')}
                                    inputProps={{
                                        style: {
                                            textAlign: isRTL ? 'right' : 'left'
                                        }
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            backgroundColor: alpha('#1E293B', 0.6),
                                            '& fieldset': { borderColor: alpha(brandColors.primary.main, 0.3) },
                                            '&:hover fieldset': { borderColor: brandColors.primary.main },
                                            '&.Mui-focused fieldset': { borderColor: brandColors.primary.light },
                                        },
                                        '& .MuiInputLabel-root': { color: '#CBD5E1' },
                                        '& .MuiInputBase-input': { color: 'white' },
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={onboardingData.isExactTime}
                                            onChange={(e) => updateData('isExactTime', e.target.checked)}
                                            sx={{ color: brandColors.primary.main }}
                                        />
                                    }
                                    label={t('onboarding.form.exactTime')}
                                    sx={{ color: '#CBD5E1' }}
                                />
                                {!onboardingData.isExactTime && (
                                    <Typography variant="body2" sx={{ mt: 1, color: '#94A3B8', fontStyle: 'italic' }}>
                                        {t('onboarding.form.exactTimeNote')}
                                    </Typography>
                                )}
                            </Grid>
                        </Grid>
                    </Box>
                );

            case 2:
                return (
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 700, mb: 2, color: 'white' }}>
                            {t('onboarding.interests.title')}
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 4, color: '#CBD5E1' }}>
                            {t('onboarding.interests.description')}
                        </Typography>

                        <Box sx={{ mb: 4 }}>
                            <Typography variant="h6" sx={{ mb: 2, color: 'white' }}>
                                {t('onboarding.interests.primaryTitle')}
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {interests.map((interest) => (
                                    <InterestChip
                                        key={interest}
                                        label={interest}
                                        selected={onboardingData.primaryInterests.includes(interest)}
                                        onClick={() => toggleArrayItem('primaryInterests', interest)}
                                    />
                                ))}
                            </Box>
                        </Box>

                        <Box sx={{ mb: 4 }}>
                            <Typography variant="h6" sx={{ mb: 2, color: 'white' }}>
                                {t('onboarding.interests.lifeAreasTitle')}
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {lifeAreas.map((area) => (
                                    <InterestChip
                                        key={area}
                                        label={area}
                                        selected={onboardingData.lifeAreas.includes(area)}
                                        onClick={() => toggleArrayItem('lifeAreas', area)}
                                    />
                                ))}
                            </Box>
                        </Box>

                        <FormControl component="fieldset" sx={{ width: '100%' }}>
                            <FormLabel component="legend" sx={{ color: 'white', mb: 2 }}>
                                {t('onboarding.interests.experienceTitle')}
                            </FormLabel>
                            <RadioGroup
                                value={onboardingData.experience}
                                onChange={(e) => updateData('experience', e.target.value)}
                            >
                                <FormControlLabel
                                    value="beginner"
                                    control={<Radio sx={{ color: brandColors.primary.main }} />}
                                    label={t('onboarding.interests.beginner')}
                                    sx={{ color: '#CBD5E1' }}
                                />
                                <FormControlLabel
                                    value="intermediate"
                                    control={<Radio sx={{ color: brandColors.primary.main }} />}
                                    label={t('onboarding.interests.intermediate')}
                                    sx={{ color: '#CBD5E1' }}
                                />
                                <FormControlLabel
                                    value="advanced"
                                    control={<Radio sx={{ color: brandColors.primary.main }} />}
                                    label={t('onboarding.interests.advanced')}
                                    sx={{ color: '#CBD5E1' }}
                                />
                            </RadioGroup>
                        </FormControl>
                    </Box>
                );

            case 3:
                return (
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 700, mb: 2, color: 'white' }}>
                            {t('onboarding.goals.title')}
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 4, color: '#CBD5E1' }}>
                            {t('onboarding.goals.description')}
                        </Typography>

                        <Box sx={{ mb: 4 }}>
                            <Typography variant="h6" sx={{ mb: 2, color: 'white' }}>
                                {t('onboarding.goals.title2')}
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {goals.map((goal) => (
                                    <InterestChip
                                        key={goal}
                                        label={goal}
                                        selected={onboardingData.goals.includes(goal)}
                                        onClick={() => toggleArrayItem('goals', goal)}
                                    />
                                ))}
                            </Box>
                        </Box>

                        <FormControl component="fieldset" sx={{ width: '100%' }}>
                            <FormLabel component="legend" sx={{ color: 'white', mb: 2 }}>
                                {t('onboarding.goals.frequencyTitle')}
                            </FormLabel>
                            <RadioGroup
                                value={onboardingData.readingFrequency}
                                onChange={(e) => updateData('readingFrequency', e.target.value)}
                            >
                                <FormControlLabel
                                    value="daily"
                                    control={<Radio sx={{ color: brandColors.primary.main }} />}
                                    label={t('onboarding.goals.daily')}
                                    sx={{ color: '#CBD5E1' }}
                                />
                                <FormControlLabel
                                    value="weekly"
                                    control={<Radio sx={{ color: brandColors.primary.main }} />}
                                    label={t('onboarding.goals.weekly')}
                                    sx={{ color: '#CBD5E1' }}
                                />
                                <FormControlLabel
                                    value="monthly"
                                    control={<Radio sx={{ color: brandColors.primary.main }} />}
                                    label={t('onboarding.goals.monthly')}
                                    sx={{ color: '#CBD5E1' }}
                                />
                                <FormControlLabel
                                    value="asneeded"
                                    control={<Radio sx={{ color: brandColors.primary.main }} />}
                                    label={t('onboarding.goals.asNeeded')}
                                    sx={{ color: '#CBD5E1' }}
                                />
                            </RadioGroup>
                        </FormControl>
                    </Box>
                );

            case 4:
                return (
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 700, mb: 2, color: 'white', textAlign: 'center' }}>
                            {t('onboarding.plan.title')}
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 4, color: '#CBD5E1', textAlign: 'center' }}>
                            {t('onboarding.plan.description')}
                        </Typography>

                        <Grid container spacing={3} justifyContent="center">
                            {/* Elite Plan */}
                            <Grid item xs={12} md={4}>
                                <PlanCard
                                    selected={onboardingData.selectedPlan === 'free'}
                                    onClick={() => updateData('selectedPlan', 'free')}
                                >
                                    <CardContent sx={{ p: 4, textAlign: 'center' }}>
                                        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, color: 'white' }}>
                                            {t('onboarding.plan.elite')}
                                        </Typography>
                                        <Typography variant="h3" sx={{ fontWeight: 700, mb: 1, color: 'white' }}>
                                            $9.99
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#CBD5E1', mb: 3 }}>
                                            {t('onboarding.plan.perMonth')}
                                        </Typography>
                                        <Box sx={{ textAlign: 'left', mb: 3 }}>
                                            <Typography variant="body2" sx={{ color: '#CBD5E1', mb: 1, display: 'flex', alignItems: 'center' }}>
                                                <Check sx={{ mr: 1, fontSize: 16, color: '#10B981' }} />
                                                {t('onboarding.plan.everythingInPro')}
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: '#CBD5E1', mb: 1, display: 'flex', alignItems: 'center' }}>
                                                <Check sx={{ mr: 1, fontSize: 16, color: '#10B981' }} />
                                                {t('onboarding.plan.unlimitedConsultations')}
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: '#CBD5E1', mb: 1, display: 'flex', alignItems: 'center' }}>
                                                <Check sx={{ mr: 1, fontSize: 16, color: '#10B981' }} />
                                                {t('onboarding.plan.premiumCompatibility')}
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: '#CBD5E1', mb: 1, display: 'flex', alignItems: 'center' }}>
                                                <Check sx={{ mr: 1, fontSize: 16, color: '#10B981' }} />
                                                {t('onboarding.plan.careerLoveForecasts')}
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: '#CBD5E1', mb: 1, display: 'flex', alignItems: 'center' }}>
                                                <Check sx={{ mr: 1, fontSize: 16, color: '#10B981' }} />
                                                {t('onboarding.plan.prioritySupport')}
                                            </Typography>
                                        </Box>
                                    </CardContent>
                                </PlanCard>
                            </Grid>
                        </Grid>

                        <Box sx={{ mt: 4, textAlign: 'center' }}>
                            <Typography variant="body2" sx={{ color: '#94A3B8', mb: 2 }}>
                                {t('onboarding.plan.freeTrial')}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#94A3B8' }}>
                                {t('onboarding.plan.terms')}
                            </Typography>
                        </Box>
                    </Box>
                );

            default:
                return null;
        }
    };

    return (
        <OnboardingContainer>
            {/* Animated Background Elements */}
            <Box sx={{ position: 'fixed', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
                {Array.from({ length: 30 }).map((_, i) => (
                    <Box
                        key={i}
                        sx={{
                            position: 'absolute',
                            left: `${Math.random() * 100}%`,
                            top: `${Math.random() * 100}%`,
                            animation: `twinkle ${2 + Math.random() * 3}s infinite`,
                            animationDelay: `${Math.random() * 3}s`,
                            '@keyframes twinkle': {
                                '0%, 100%': { opacity: 0.3 },
                                '50%': { opacity: 0.8 },
                            },
                        }}
                    >
                        <Star sx={{ fontSize: 8, color: 'white' }} />
                    </Box>
                ))}
            </Box>

            <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, py: 4 }}>
                {/* Progress Header */}
                <Box sx={{ mb: 6, textAlign: 'center' }}>
                    <Box sx={{ mb: 4 }}>
                        {(() => {
                            const stepText = t('onboarding.stepOf', { 
                                current: activeStep + 1, 
                                total: steps.length 
                            });
                            return (
                                <Typography variant="body1" sx={{ color: '#CBD5E1', mb: 2 }}>
                                    {stepText !== 'onboarding.stepOf' ? stepText : `Step ${activeStep + 1} of ${steps.length}`}
                                </Typography>
                            );
                        })()}
                        <LinearProgress
                            variant="determinate"
                            value={((activeStep + 1) / steps.length) * 100}
                            sx={{
                                height: 8,
                                borderRadius: 4,
                                backgroundColor: alpha('#1E293B', 0.6),
                                '& .MuiLinearProgress-bar': {
                                    background: `linear-gradient(90deg, ${brandColors.primary.main}, ${brandColors.secondary.main})`,
                                    borderRadius: 4,
                                },
                            }}
                        />
                    </Box>
                </Box>

                {/* Desktop Stepper */}
                {!isMobile && (
                    <Box sx={{ mb: 6 }}>
                        <Stepper activeStep={activeStep} orientation="horizontal">
                            {steps.map((step, index) => (
                                <Step key={index}>
                                    <StepLabel
                                        sx={{
                                            '& .MuiStepLabel-root': {
                                                color: '#CBD5E1',
                                            },
                                            '& .MuiStepIcon-root': {
                                                color: index <= activeStep ? brandColors.primary.main : '#64748B',
                                                '&.Mui-active': {
                                                    color: brandColors.primary.main,
                                                },
                                                '&.Mui-completed': {
                                                    color: brandColors.secondary.main,
                                                },
                                            },
                                            '& .MuiStepLabel-label': {
                                                color: index <= activeStep ? 'white' : '#94A3B8',
                                                fontWeight: index === activeStep ? 600 : 400,
                                            },
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            {step.icon}
                                            <Box>
                                                <Typography variant="body1" sx={{ fontWeight: index === activeStep ? 600 : 400 }}>
                                                    {step.title}
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: '#94A3B8' }}>
                                                    {step.subtitle}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </StepLabel>
                                </Step>
                            ))}
                        </Stepper>
                    </Box>
                )}

                {/* Main Content */}
                <StepCard sx={{ maxWidth: 800, mx: 'auto' }}>
                    <CardContent sx={{ p: { xs: 4, md: 6 } }}>
                        {renderStepContent(activeStep)}
                    </CardContent>
                </StepCard>

                {/* Navigation Buttons */}
                <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between', maxWidth: 800, mx: 'auto' }}>
                    <Button
                        onClick={handleBack}
                        disabled={activeStep === 0}
                        startIcon={isRTL ? <ChevronRight /> : <ChevronLeft />}
                        sx={{
                            color: '#CBD5E1',
                            borderColor: alpha(brandColors.primary.main, 0.3),
                            gap: '0 16px',
                            '&:hover': {
                                borderColor: brandColors.primary.main,
                                backgroundColor: alpha(brandColors.primary.main, 0.1),
                            },
                            '&:disabled': {
                                color: '#64748B',
                                borderColor: '#334155',
                            },
                        }}
                        variant="outlined"
                    >
                        {t('onboarding.back')}
                    </Button>

                    {activeStep === steps.length - 1 ? (
                        <BrandButton
                            onClick={handleComplete}
                            disabled={!isStepValid(activeStep)}
                            endIcon={<Celebration />}
                            size="large"
                        >
                            {t('onboarding.completeSetup')}
                        </BrandButton>
                    ) : (
                        <BrandButton
                            onClick={handleNext}
                            disabled={!isStepValid(activeStep)}
                            endIcon={isRTL ? <ChevronLeft /> : <ChevronRight />}
                            size="large"
                        >
                            {t('onboarding.continue')}
                        </BrandButton>
                    )}
                </Box>

                {/* Step Indicator for Mobile */}
                {isMobile && (
                    <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center', gap: 1 }}>
                        {steps.map((_, index) => (
                            <Box
                                key={index}
                                sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    backgroundColor: index <= activeStep ? brandColors.primary.main : '#64748B',
                                    transition: 'all 0.3s ease',
                                }}
                            />
                        ))}
                    </Box>
                )}

                {/* Footer */}
                <Box sx={{ mt: 8, textAlign: 'center' }}>
                    <Typography variant="body2" sx={{ color: '#64748B' }}>
                        {t('onboarding.dataSecure')}
                    </Typography>
                </Box>
            </Container>
        </OnboardingContainer>
    );
};

export default OnBoarding;